import dayjs from 'dayjs'
import { ActionEnum, CategoryEnum } from 'enums/analytics'
import { pusherChannels } from 'enums/constants/pusher'
import compact from 'lodash/compact'
import { UseFormGetValues, UseFormReset } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  DATE_OF_BIRTH_PATTERN,
  EMAIL_PATTERN,
  MONTHLY_INCOME_PATTERN,
  ZIP_CODE_PATTERN,
  NAME_PATTERN,
  NAME_WITH_NUMBER_PATTERN,
  RAW_PHONE_NUMBER_PATTERN
} from 'constants/common'
import { IApplicationFormValues, IHouseApplication } from 'interfaces/application'
import { logEvent } from 'utils/analytics'
import { getRawUSPhoneNumber, getValidArray } from 'utils/common'
import { EApplicationCompletedState } from '../../ApplicationFormModal/constant'
import { EApplicationCompletedStateV3 } from '../../ApplicationFormModalV3/constant'
import { EApplicationStepBar, PAYSTUBS_REQUIRED_QUANTITY, EApplicationCompletedStateV2 } from '../constant'

export function logStepToAmplitude(step: number): void {
  switch (step) {
    case EApplicationStepBar.DO_YOU_QUALIFY:
      logEvent(CategoryEnum.APPLICATION_FORM, ActionEnum.PERSONAL_INFO_STEP_LOADED)
    default:
      break
  }
}

export function validateFormStep(
  activeStep: number,
  formValue: IApplicationFormValues,
  isDoneCompleteScreening: boolean,
  isMobileTablet: boolean,
  isGeneralApplication: boolean = true
): boolean {
  switch (activeStep) {
    case EApplicationStepBar.DO_YOU_QUALIFY:
      if (formValue?.confirm && formValue?.email && formValue?.firstName && formValue?.lastName) {
        //* INFO: Validate Email Address and name, allow phone number as name
        const isValidEmail: boolean = EMAIL_PATTERN.test(formValue.email)
        const isValidFirstName: boolean =
          NAME_PATTERN.test(formValue.firstName) || RAW_PHONE_NUMBER_PATTERN.test(formValue.firstName)
        const isValidLastName: boolean =
          NAME_PATTERN.test(formValue.lastName) || RAW_PHONE_NUMBER_PATTERN.test(formValue.lastName)
        const isValidPhoneNumber: boolean = isGeneralApplication
          ? RAW_PHONE_NUMBER_PATTERN.test(getRawUSPhoneNumber(formValue?.phoneNumber!))
          : true
        return isValidEmail && isValidFirstName && isValidLastName && isValidPhoneNumber
      }
      return false

    case EApplicationStepBar.SHARE_YOUR_DEETS:
      if (formValue?.monthlyIncome && !MONTHLY_INCOME_PATTERN.test(String(formValue?.monthlyIncome))) {
        return false
      }
      if (
        formValue?.ssn &&
        !formValue?.ssn?.includes('X') &&
        formValue?.addressLine1 &&
        formValue?.city &&
        formValue?.state &&
        formValue?.zipCode &&
        ZIP_CODE_PATTERN.test(formValue.zipCode)
      ) {
        //* INFO: Validate dateOfBirth on desktop
        if (formValue?.dateOfBirth && dayjs(formValue?.dateOfBirth).isValid() && !isMobileTablet) {
          //* INFO Check if dateOfBirth is valid
          return DATE_OF_BIRTH_PATTERN.test(formValue.dateOfBirth)
        }
        //* INFO: Validate dateOfBirth on mobile & tablet
        const dateOfBirth: string = `${formValue?.month ?? 0}/${formValue?.day ?? 0}/${formValue?.year ?? 0}`
        if (Boolean(dayjs(dateOfBirth).isValid() && formValue?.month && formValue?.day && formValue?.year)) {
          return DATE_OF_BIRTH_PATTERN.test(dateOfBirth)
        }

        return false
      }
      return false

    case EApplicationStepBar.COMPLETE_SCREENING:
      if (formValue?.previousLandlordPhoneNumber || formValue?.previousLandlordName) {
        const isValidPhoneNumber: boolean = formValue?.previousLandlordPhoneNumber
          ? RAW_PHONE_NUMBER_PATTERN.test(formValue.previousLandlordPhoneNumber)
          : true
        const isValidName: boolean = formValue?.previousLandlordName
          ? NAME_WITH_NUMBER_PATTERN.test(formValue.previousLandlordName)
          : true
        const isValidEmail: boolean = formValue?.previousLandlordEmail
          ? EMAIL_PATTERN.test(formValue.previousLandlordEmail)
          : true
        return isValidPhoneNumber && isValidName && isValidEmail
      }
      return isDoneCompleteScreening

    default:
      return true
  }
}

export async function prefillClientInfo(
  houseApplication: IHouseApplication,
  reset: UseFormReset<IApplicationFormValues>,
  getValues: UseFormGetValues<IApplicationFormValues>
): Promise<void> {
  const formValue: IApplicationFormValues = getValues()

  let prefillData: Partial<IApplicationFormValues> = {
    phoneNumber: houseApplication.phoneNumber,
    firstName: houseApplication.firstName,
    lastName: houseApplication.lastName,
    email: houseApplication.email,
    ssn: houseApplication.ssn,
    zipCode: houseApplication.zipCode,
    houseNumber: houseApplication?.houseNumber,
    streetName: houseApplication?.streetName,
    city: houseApplication?.city,
    state: houseApplication?.state,
    addressLine1: houseApplication?.addressLine1,
    addressLine2: houseApplication?.addressLine2,
    driverLicenseFrontPhotoUrl: houseApplication?.driverLicenseFrontPhotoUrl,
    driverLicenseBackPhotoUrl: houseApplication?.driverLicenseBackPhotoUrl,
    monthlyIncome: houseApplication?.monthlyIncome,
    previousLandlordName: houseApplication?.previousLandlordName,
    previousLandlordPhoneNumber: houseApplication?.previousLandlordPhoneNumber,
    previousLandlordEmail: houseApplication?.previousLandlordEmail,
    previousMoveInDate: houseApplication?.previousMoveInDate
      ? new Date(houseApplication?.previousMoveInDate)
      : undefined,
    previousMoveOutDate: houseApplication?.previousMoveOutDate
      ? new Date(houseApplication?.previousMoveOutDate)
      : undefined
  }
  //* INFO: Handle dateOfBirth field in case mobile or desktop/tablet
  if (houseApplication.dateOfBirth && dayjs(houseApplication.dateOfBirth).isValid()) {
    const currentDateOfBirth: string = houseApplication.dateOfBirth
    prefillData.dateOfBirth = currentDateOfBirth
    // INFO: prefill data dateOfBirth on Mobile screen
    prefillData.month = dayjs(currentDateOfBirth).format('MM')
    prefillData.day = dayjs(currentDateOfBirth).format('DD')
    prefillData.year = dayjs(currentDateOfBirth).format('YYYY')
  }
  reset({ ...formValue, ...prefillData })
}

export function getReferrers(formValue: IApplicationFormValues, filteredFormValue: IApplicationFormValues): string[] {
  const referrers: string[] = getValidArray<string>([
    ...getValidArray<string>(filteredFormValue?.referrers),
    ...(formValue?.otherReferrer ? [formValue.otherReferrer] : [])
  ])
  return compact(referrers)
}

export function getDriverLicenseChannel(phoneNumber: string): string {
  if (!phoneNumber) {
    return ''
  }

  // INFO: remove "+" char in phone number to make chanel id
  const customerId: string = `${phoneNumber}`.replace('+', '')
  const driverLicenseChannel: string = pusherChannels?.driverLicense?.verified?.value(customerId)

  return driverLicenseChannel
}

export function notifyPaystubsRequiredQuantity(): void {
  toast.error(`Please upload at least ${PAYSTUBS_REQUIRED_QUANTITY} paystubs`, {
    toastId: 'paystub-quantity-error'
  })
}

export function getNewCompletedState(lastCompletedStep: number, lastCompletedState?: string): string {
  const completedStateListV3: EApplicationCompletedStateV3[] = Object.values(EApplicationCompletedStateV3)
  if (!lastCompletedState) {
    return completedStateListV3[0]
  }
  console.log(completedStateListV3)
  if (!completedStateListV3.includes(lastCompletedState as EApplicationCompletedStateV3)) {
    return completedStateListV3[1]
  }
  return completedStateListV3[lastCompletedStep + 1]
}

export function getContinueApplyingStep(
  lastCompletedState?: EApplicationCompletedState | EApplicationCompletedStateV2 | EApplicationCompletedStateV3
): number {
  const completedStateListV3: EApplicationCompletedStateV3[] = Object.values(EApplicationCompletedStateV3)
  if (!lastCompletedState || !completedStateListV3.includes(lastCompletedState as EApplicationCompletedStateV3)) {
    return 0
  }
  return completedStateListV3.indexOf(lastCompletedState as EApplicationCompletedStateV3) - 1
}

export function checkValidDayOfMonth(month: number, day: number, year: number): boolean {
  const date: Date = new Date(year, month - 1, day) // *INFO: month index is 0-11
  const isValidDay: boolean = date.getDate() === day
  const isValidMonth: boolean = date.getMonth() === month - 1
  const isValidYear: boolean = date.getFullYear() === year
  // *INFO: check if year or month is NaN (Number(undefined) = NaN))
  if (!month) {
    return isValidDay
  }
  if (!year) {
    return isValidDay && isValidMonth
  }
  return isValidDay && isValidMonth && isValidYear
}

export function validatePreviousMoveDate(previousMoveDate: [Date, Date]): (Date | undefined)[] {
  return getValidArray(previousMoveDate).map((date: Date) => {
    const formattedDate: dayjs.Dayjs = dayjs(date).add(1, 'day')
    return date && formattedDate.isValid() ? formattedDate.toDate() : undefined
  })
}
